import { useMutation } from '@apollo/client';
import { Typography, List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import SearchInput from '../../common/inputs/searchInput';
import { UPDATE_USER } from '../graphql';
import { professions } from '../resources';
import { ovAnalyticsEvents, sendAnalyticsEvent } from '../../../utils/firebase';
import GeneralListItem from '../../common/lists/generalListItem';
import OvForm from '../../common/wrappers/ovForm';

export interface UserState {
  jobTitle?: string,
  companyType?: string,
  employmentStatus?: string,
  incompleteFields?: string[],
}

interface Props {
  user: UserState,
  updateUserState: (att: UserState) => void,
  onContinue?: () => void,
  cached?: boolean,
  goToNamedStep?: (step: string) => void,
  title?: string,
  subTitle?: string,
  analyticsEvent?: string,
}
interface Profession {
  profession: string,
  jobTitles: string[],
}

const EmployeeJobTitle = ({
  user, updateUserState, cached, title, subTitle, analyticsEvent, ...props
}: Props): JSX.Element => {
  const { t } = useTranslation(['base', 'user']);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const jobInfo = professions.filter((item: Profession) => item.profession === user.companyType);
  const { jobTitles } = jobInfo[0] || [];
  const [state, setState] = useState({
    value: '',
    sourceData: jobTitles,
    filterData: jobTitles,
  });
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const filterList = (e: any): void => {
    if (e.target && e.target.value) {
      const updatedList = state.sourceData.filter((item) => (item.toLowerCase().search(e.target.value.toLowerCase()) !== -1));
      setState({ ...state, filterData: updatedList });
    }
  };
  const updateJobTitleOnCompleted = (data: { updateUser: { user: { incompleteFields: string[] } } }): void => {
    console.log({ event: 'JOB_TITLE_UPDATED', jobTitle: user.jobTitle });
    updateUserState({ ...user, incompleteFields: data.updateUser.user.incompleteFields });
    setLoading(false);
    if (props.onContinue) props.onContinue();
  };
  const [updateEmployementStatus] = useMutation(UPDATE_USER, {
    variables: {
      input: { jobTitle: user.jobTitle },
    },
    onCompleted: updateJobTitleOnCompleted,
    onError: () => setLoading(false),
  });
  const onContinueClick = (): void => {
    sendAnalyticsEvent(analyticsEvent ?? ovAnalyticsEvents.onBoardingEnterRoleAtCompany).then();
    if (cached) {
      if (props.onContinue) props.onContinue();
    } else {
      setLoading(true);
      updateEmployementStatus().then();
    }
  };
  const [clickedValue, setClickedValue] = useState('');
  const subTitleText = user && user.employmentStatus && (user.employmentStatus === 'EMPLOYED' || user.employmentStatus === 'SELF_EMPLOYED') ? t('user:employement.jobTitle.titleForEmployee') : t('user:employement.jobTitle.titleForRetiered');
  return (
    <OvForm loading={loading} onSubmit={onContinueClick} disableButton={!user.jobTitle} submitButton={false}>
      <Typography variant="heading2">{title ?? subTitleText}</Typography>
      <Typography variant="paragraph3">{subTitle ?? t('user:employement.jobTitle.subTitle')}</Typography>
      <SearchInput
        label={t('user:employement.jobTitle.placeholder')}
        value={searchString}
        onChange={(event) => {
          setSearchString(event.target.value);
          filterList(event);
        }}
        onClear={() => {
          setState({ ...state, filterData: jobTitles });
          setClickedValue('');
          setSearchString('');
          updateUserState({ ...user, jobTitle: '' });
        }}
      />
      <List className="on-boarding-list">
        {state.filterData.map((value: string) => (
          <GeneralListItem
            text={value}
            key={value}
            hasRadioField
            checked={clickedValue === value || value === user.jobTitle}
            onClick={() => {
              if (value) {
                setSearchString(value);
                setClickedValue(value);
                updateUserState({ ...user, jobTitle: value });
              }
            }}
          />
        ))}
      </List>
    </OvForm>
  );
};

EmployeeJobTitle.defaultProps = {
  onContinue: undefined,
  goToNamedStep: undefined,
  cached: false,
  title: undefined,
  subTitle: undefined,
  analyticsEvent: undefined,
};

export default EmployeeJobTitle;
